import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useState } from "react";

import { useWorkspace } from "../-workspace-context";
import { heathEmail } from "../../../auth/heath";
import { AppsRow } from "../../apps/AppsRow";
import { TrialCard } from "../../billing/TrialCard";
import { ChatComposer } from "../../chats/ChatComposer";
import { InstallButton } from "../../components/InstallButton";
import { TitleBar } from "../../components/TitleBar";
import { Pinboard } from "../../pinboard/Pinboard";

// Lazy: SummaryCard drags Streamdown into the home chunk, and it only renders
// on the one-shot `?summary=` hand-off.
const SummaryCard = lazy(() =>
  import("../../onboarding/SummaryCard").then((m) => ({
    default: m.SummaryCard,
  })),
);

export const Route = createFileRoute("/_workspace/")({
  // `summary` is the interviewer's one-shot hand-off, passed via the URL (never
  // server-persisted) — read and stripped on mount below.
  validateSearch: (search: Record<string, unknown>): { summary?: string } => ({
    summary: typeof search.summary === "string" ? search.summary : undefined,
  }),
  head: () => ({ meta: [{ title: "Home" }] }),
  component: HomePage,
});

function HomePage() {
  const { pinned, playground, openFile, apps, openApp, identity } =
    useWorkspace();
  const navigate = useNavigate();
  const emailAddress = heathEmail(identity.tenant);
  // The interviewer hands its summary off through the URL. Capture it once into
  // state and strip it from the URL so a refresh doesn't re-surface it; the X
  // on the card drops it from view.
  const { summary } = Route.useSearch();
  const [onboardingSummary, setOnboardingSummary] = useState<string | null>(
    summary ?? null,
  );
  useEffect(() => {
    if (summary) navigate({ to: "/", search: {}, replace: true });
  }, [summary, navigate]);

  const goToChat = (chatId: string) => {
    navigate({ to: "/chats/$chatId", params: { chatId }, search: {} });
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* The title bar exists on mobile only (desktop uses the global header):
          it carries the open-files button and, top-right, the install
          affordance — which self-hides once Hearth is installed. */}
      <TitleBar compactOnly actions={<InstallButton iconOnly />}>
        <span className="font-serif text-lg font-semibold tracking-tight">
          Hearth
        </span>
      </TitleBar>
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-3xl px-6 pt-10 pb-12">
          <ChatComposer onChatStarted={goToChat} />
          <p className="mt-2 text-center text-xs text-muted-foreground">
            Chat with Heath by email at{" "}
            <a
              href={`mailto:${emailAddress}`}
              className="underline underline-offset-2 hover:text-foreground"
            >
              {emailAddress}
            </a>
          </p>
          {onboardingSummary && (
            <div className="mt-8">
              <Suspense fallback={null}>
                <SummaryCard
                  summary={onboardingSummary}
                  onDismiss={() => setOnboardingSummary(null)}
                />
              </Suspense>
            </div>
          )}
          <TrialCard />
          <AppsRow apps={apps.entries} onOpen={openApp} />
          <Pinboard
            paths={pinned.paths}
            playground={playground}
            apps={apps.entries}
            onOpen={openFile}
          />
        </div>
      </div>
    </div>
  );
}
